"use client";

import axios from "axios";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  lookupPincode,
  lookupPincodeFallback,
} from "@/shared/services/location.service";

export type PincodeRegion = {
  city: string;
  district: string;
  state: string;
  country: string;
};

export type UsePincodeLookupOptions = {
  pin: string | null;
  enabled?: boolean;
  debounceMs?: number;
};

const DEFAULT_DEBOUNCE_MS = 350;

export function isValidSixDigitPin(pin: string | null | undefined): pin is string {
  return !!pin && /^\d{6}$/.test(pin);
}

type PinComponentsWire = {
  city?: string | null;
  district?: string | null;
  state?: string | null;
  country?: string | null;
};

type PinResponseWire = {
  success?: boolean;
  data?: { components?: PinComponentsWire | null } | null;
};

function parsePincodeBody(body: unknown): PincodeRegion {
  if (body === null || typeof body !== "object") {
    throw new Error("No data returned for this PIN");
  }
  const wire = body as PinResponseWire;
  if (wire.success !== true || !wire.data?.components) {
    throw new Error("PIN not found or invalid response");
  }
  const c = wire.data.components;
  const city = typeof c.city === "string" ? c.city.trim() : "";
  const district = typeof c.district === "string" ? c.district.trim() : "";
  const state = typeof c.state === "string" ? c.state.trim() : "";
  const countryRaw = typeof c.country === "string" ? c.country.trim() : "";

  if (!city) {
    throw new Error("Could not resolve city for this PIN");
  }
  if (!district || !state) {
    throw new Error("Incomplete address data for this PIN");
  }

  return {
    city,
    district,
    state,
    country: countryRaw || "India",
  };
}

type FallbackEntryWire = {
  Name?: string | null;
  District?: string | null;
  State?: string | null;
  Country?: string | null;
};

type FallbackResponseWire = {
  success?: boolean;
  data?: FallbackEntryWire[] | null;
};

function parseFallbackBody(body: unknown): PincodeRegion {
  if (body === null || typeof body !== "object") {
    throw new Error("No data returned for this PIN");
  }
  const wire = body as FallbackResponseWire;
  if (wire.success !== true || !Array.isArray(wire.data) || wire.data.length === 0) {
    throw new Error("PIN not found");
  }
  const entry = wire.data[0]!;
  const district = typeof entry.District === "string" ? entry.District.trim() : "";
  const state = typeof entry.State === "string" ? entry.State.trim() : "";
  const country = typeof entry.Country === "string" ? entry.Country.trim() : "";

  if (!district || !state) {
    throw new Error("Incomplete address data for this PIN");
  }

  return {
    city: district,
    district,
    state,
    country: country || "India",
  };
}

function isAbortError(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") return true;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return false;
}

async function lookupPincodeWithFallback(
  pin: string,
  signal: AbortSignal | undefined,
): Promise<PincodeRegion> {
  try {
    const body = await lookupPincode(pin, signal);
    return parsePincodeBody(body);
  } catch (primaryError) {
    if (isAbortError(primaryError)) throw primaryError;
    const fallbackBody = await lookupPincodeFallback(pin, signal);
    return parseFallbackBody(fallbackBody);
  }
}

export function usePincodeLookup({
  pin,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UsePincodeLookupOptions) {
  const [debouncedPin, setDebouncedPin] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !isValidSixDigitPin(pin)) {
      setDebouncedPin(null);
      return;
    }
    const t = window.setTimeout(() => setDebouncedPin(pin), debounceMs);
    return () => window.clearTimeout(t);
  }, [pin, enabled, debounceMs]);

  const queryEnabled = enabled && isValidSixDigitPin(debouncedPin);

  const query = useQuery<PincodeRegion, Error>({
    queryKey: ["pincode-lookup", debouncedPin],
    queryFn: ({ signal }) => lookupPincodeWithFallback(debouncedPin!, signal),
    enabled: queryEnabled,
    retry: 0,
  });

  return {
    ...query,
    debouncedPin,
  };
}
